'use client'
import { SbBlokData, storyblokEditable, StoryblokServerComponent } from '@storyblok/react/rsc'
import { FC, JSX, useEffect, useState } from 'react'

import { GoogleReviews } from '@/components/GoogleReviews'
import { searchParamsData } from '@/lib/sbAPI'
import { getStoryblokApi } from '@/lib/storyblok'

import AdviserDetails from '../../02-molecules/AdviserDetails/AdviserDetails'
import { PhnxTabGroup } from '../../02-molecules/TabGroup/TabGroup'
import { PhnxAdvanceoffice } from './Advanceoffice'
import { AdvanceofficeProps } from './Advanceoffice'
import styles from './Advanceoffice.module.css'

interface LocationBlock extends SbBlokData {
  component: 'location'
  latitude: string
  longitude: string
}

interface OfficeBlock extends SbBlokData {
  content: LocationBlock[]
}

// interface Sponsor {
//   link: string
//   src: string
// }

interface TabContent {
  name: string

  content: JSX.Element
}

interface AdvanceofficeBlokProps {
  blok: {
    title: AdvanceofficeProps['title']
    description: SbBlokData[]
    sponsors: any[]
    testimonials: any[]
    office: OfficeBlock[]
    tabContent: any
  } & SbBlokData
  uuid: string
  rels?: any
}

const Advanceoffice: FC<AdvanceofficeBlokProps> = ({ blok, uuid, rels }) => {
  const [tabContentArray, setTabContentArray] = useState<TabContent[]>([])
  const [advisers, setAdvisers] = useState<any[]>([])
  const [sortedAdvisers, setSortedAdvisers] = useState<any[]>([])

  // const contentVersion = process.env.NEXT_PUBLIC_SB_VERSION
  //   ? process.env.NEXT_PUBLIC_SB_VERSION
  //   : 'published'

  const location = process.env.NEXT_PUBLIC_NEXT_LOCATION || 'au'
  const filterLocation = location === 'en-nz' ? 'au' : 'en-nz'

  useEffect(() => {
    const fetchAdviserData = async () => {
      const storyblokApi = getStoryblokApi()
      if (!storyblokApi) {
        console.error('Storyblok API client not initialized')
        return
      }

      const sbParams = {
        ...searchParamsData,
        filter_query: {
          component: { in: 'adviser' },
          offices: { any_in_array: uuid },
          __or: [
            { 'settings.location_available': { in: location } },
            { 'settings.location_available': { not_in: filterLocation } },
          ],
        },
        sort_by: 'content.title:asc',
        per_page: 100,
      }

      try {
        const { data: advisersData } = await storyblokApi.get('cdn/stories', sbParams)
        setAdvisers(advisersData?.stories || [])
        return advisersData?.stories || []
      } catch (error) {
        console.error('Failed to fetch adviser data:', error)
        return []
      }
    }

    fetchAdviserData()
  }, [blok.tabContent, uuid, rels, location, filterLocation])

  useEffect(() => {
    const allAdvisors = advisers
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((story: any) => {
        return {
          title: story.name,
          role: story.content.title,
          link: `/adviser/${story.slug}`,
          avatarsrc: story.content.image.filename,
          avataralt: story.content.image.alt,
          biography: Array.isArray(story.content.bio)
            ? story.content.bio.map((child: SbBlokData) => (
                <StoryblokServerComponent blok={child} key={child._uid} />
              ))
            : null,
          specialties: story.content.specialities.map((speciality: any) => ({
            title: speciality,
            href: `/advisers/search?specialty=${speciality}`,
          })),
        }
      })
    setSortedAdvisers(allAdvisors)
  }, [advisers])

  useEffect(() => {
    if (blok.tabContent) {
      const itemsArray = blok.tabContent.map((item: any) => ({
        name: item.tabName,
        content:
          item.tabContent &&
          item.tabContent.length > 0 &&
          item.tabContent[0].component === 'adviserDetails'
            ? sortedAdvisers.map((item, index) => (
                <AdviserDetails
                  key={'advisor' + index}
                  avatar={{ src: item.avatarsrc, alt: item.avataralt }}
                  name={item.title}
                  title={item.title}
                  role={item.role}
                  link={item.link}
                  specialties={item.specialties}
                  biography={item.biography}
                />
              ))
            : item.tabContent.map((nestedBlok: SbBlokData) => (
                <>
                  {blok.office?.map((officeBlok: SbBlokData) => (
                    <div className={styles['mob-office-details']} key={officeBlok._uid}>
                      <StoryblokServerComponent blok={{ ...officeBlok }} />
                    </div>
                  ))}
                  <StoryblokServerComponent blok={nestedBlok} key={nestedBlok._uid} />
                  {blok.testimonials?.map((nestedBlok: SbBlokData) => (
                    <div className={styles['mob-testimonies']} key={nestedBlok._uid}>
                      <StoryblokServerComponent blok={nestedBlok} key={nestedBlok._uid} />
                    </div>
                  ))}
                  {blok.sponsors?.map((nestedBlok: SbBlokData) => (
                    <div className={styles['mob-office-details']} key={nestedBlok._uid}>
                      <StoryblokServerComponent blok={nestedBlok} />
                    </div>
                  ))}
                </>
              )),
      }))
      setTabContentArray(itemsArray)
    }
  }, [blok.tabContent, sortedAdvisers])

  return (
    <div {...storyblokEditable(blok)}>
      <PhnxAdvanceoffice
        title={blok.title}
        description={blok.description?.map((nestedBlok: SbBlokData) => (
          <StoryblokServerComponent blok={nestedBlok} key={nestedBlok._uid} />
        ))}
        tabContent={<PhnxTabGroup tabs={tabContentArray as any} />}
        testimonials={blok.testimonials?.map((review: any) => (
          <StoryblokServerComponent blok={review} key={review._uid} />
        ))}
        sponsors={blok.sponsors?.map((nestedBlok: SbBlokData) => (
          <StoryblokServerComponent blok={nestedBlok} key={nestedBlok._uid} />
        ))}
        office={blok.office?.map((nestedBlok: OfficeBlock) => {
          return <StoryblokServerComponent blok={nestedBlok} key={nestedBlok._uid} />
        })}
        reviews={
          blok.office[0]?.placeId ? (
            <GoogleReviews placeId={String(blok.office[0].placeId)} />
          ) : null
        }
      />
    </div>
  )
}

export default Advanceoffice
